import { useEffect, useRef } from 'react';

/**
 * Registers a best-effort beacon when the user leaves the page (tab close, refresh, navigate away)
 * so the backend can mark the screening candidate as "session ended" and force-complete their
 * status to Completed. Uses sendBeacon for reliability when the page is unloading.
 *
 * Use only on **active** steps (e.g. AssessmentTake, live interview). Do **not** mount this on
 * join/hub/resume pages — a simple refresh would force-complete assessment/interview as abandoned.
 *
 * @param {string|null} screeningId - Screening ID (from params or state)
 * @param {string} email - Candidate email (from state or query)
 */
export function useScreeningSessionEndBeacon(screeningId, email) {
  const sentRef = useRef(false);

  useEffect(() => {
    const sid = (screeningId && String(screeningId).trim()) || null;
    const em = (email && String(email).trim().toLowerCase()) || '';
    if (!sid || !em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      return;
    }

    const send = () => {
      if (sentRef.current) return;
      sentRef.current = true;
      try {
        const url = `/api/screenings/${sid}/candidate-session-end`;
        const body = JSON.stringify({ email: em });
        const blob = new Blob([body], { type: 'application/json' });
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, blob);
        }
      } catch {
        // ignore
      }
    };

    const handleUnload = () => send();
    const handlePageHide = () => send();

    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [screeningId, email]);
}
